import {CopyToClipboardButton} from '@github-ui/copy-to-clipboard/Button'
// eslint-disable-next-line import/no-namespace
import * as Octicons from '@primer/octicons-react'
import {ActionList, ActionMenu, Text, TextInput} from '@primer/react'
import {useId} from 'react'

import type {
  ProductAnnouncementModalDropdown as ProductAnnouncementModalDropdownData,
  ProductAnnouncementModalDropdownItem,
  ProductAnnouncementModalEvent,
} from './../../../ProductAnnouncement/ProductAnnouncement.types'

import Styles from './ProductAnnouncementModalDropdown.module.css'

const ICON_SIZE = 20

interface ProductAnnouncementModalDropdownProps {
  data: ProductAnnouncementModalDropdownData
  onEvent?: (event: ProductAnnouncementModalEvent, data?: Record<string, unknown>) => void
}

const ProductAnnouncementModalDropdown = ({data, onEvent}: ProductAnnouncementModalDropdownProps) => {
  const inputId = useId()

  const getItemIcon = (icon: ProductAnnouncementModalDropdownItem['icon']) => {
    if (!icon) return null

    if (typeof icon === 'string') {
      const IconComponent = Octicons[icon]
      return IconComponent ? <IconComponent size={ICON_SIZE} /> : null
    }

    if (typeof icon === 'function') {
      const IconComponent = icon
      return <IconComponent size={ICON_SIZE} />
    }

    return (
      <img
        src={icon.src}
        alt={icon.alt ?? ''}
        width={ICON_SIZE}
        height={ICON_SIZE}
        className={Styles['ProductAnnouncementModalDropdown__item-icon']}
      />
    )
  }

  return (
    <ActionMenu
      onOpenChange={nextOpen => {
        if (nextOpen) {
          onEvent?.('tab_dropdown_opened', {label: data.label})
        }
      }}
    >
      <ActionMenu.Button>{data.label}</ActionMenu.Button>

      <ActionMenu.Overlay width="medium">
        {data.sections.map((section, index) => {
          if (section.type === 'input') {
            const sectionInputId = `${inputId}-${index}`

            return (
              <div key={sectionInputId} className={Styles['ProductAnnouncementModalDropdown__input']}>
                {section.label && (
                  <Text
                    as="label"
                    htmlFor={sectionInputId}
                    size="small"
                    className={Styles['ProductAnnouncementModalDropdown__input-label']}
                  >
                    {section.label}
                  </Text>
                )}

                <div className={Styles['ProductAnnouncementModalDropdown__input-content']}>
                  <TextInput
                    id={sectionInputId}
                    aria-label={section.label ?? data.label}
                    value={section.value}
                    readOnly
                    block
                  />
                  <CopyToClipboardButton textToCopy={section.value} ariaLabel={section.copyButtonLabel ?? ''} />
                </div>
              </div>
            )
          }

          return (
            <ActionList key={section.heading ?? section.items.map(item => item.id).join('-')}>
              <ActionList.Group>
                {section.heading && <ActionList.GroupHeading>{section.heading}</ActionList.GroupHeading>}

                {section.items.map(item => {
                  const trailingVisual = getItemIcon(item.icon)

                  if (item.href) {
                    return (
                      <ActionList.LinkItem
                        key={item.id}
                        href={item.href}
                        target={item.isNewWindow ? '_blank' : undefined}
                        onClick={() =>
                          onEvent?.('tab_dropdown_item_clicked', {id: item.id, label: item.label, url: item.href})
                        }
                      >
                        {item.label}
                        {trailingVisual && <ActionList.TrailingVisual>{trailingVisual}</ActionList.TrailingVisual>}
                      </ActionList.LinkItem>
                    )
                  }

                  return (
                    <ActionList.Item
                      key={item.id}
                      onSelect={() => onEvent?.('tab_dropdown_item_clicked', {id: item.id, label: item.label})}
                    >
                      {item.label}
                      {trailingVisual && <ActionList.TrailingVisual>{trailingVisual}</ActionList.TrailingVisual>}
                    </ActionList.Item>
                  )
                })}
              </ActionList.Group>
            </ActionList>
          )
        })}
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

export default ProductAnnouncementModalDropdown
